import { adminApi as api } from '@core/api/httpClient'
import { VASP_CARD_STATUS_FILTERS } from '@shared/constants/vaspStatus'
import type { OwnerBankAccountLookup } from './contracts/propertyOwnerContracts'
import type {
  ApproveAmlPayload,
  ApprovePepPayload,
  CreatePropertyOwnerBankAccountPayload,
  GetPropertyOwnerBankAccountsAdminParams,
  GetPropertyOwnerKycListParams,
  GetPropertyOwnerListParams,
  GetPropertyOwnerVaspListParams,
  PropertyOwner,
  PropertyOwnerBankAccountListResponse,
  PropertyOwnerBankAccountRow,
  PropertyOwnerBankAccountStatusCounts,
  PropertyOwnerAmlSubmissionRow,
  PropertyOwnerCompanyProfile,
  PropertyOwnerComplianceActivityResponse,
  PropertyOwnerComplianceActivityRow,
  PropertyOwnerComplianceProfile,
  PropertyOwnerKycDocumentSignedUrl,
  PropertyOwnerKycSubmissionRow,
  PropertyOwnerOnboardingChecks,
  PropertyOwnerOnboardingPipelineRow,
  PropertyOwnerPepSubmissionRow,
  PropertyOwnerKycDocument,
  PropertyOwnerKycDocumentsResponse,
  PropertyOwnerKycListResponse,
  PropertyOwnerListResponse,
  PropertyOwnerLookup,
  PropertyOwnerWithBankAccounts,
  OwnerBankAccountOption,
  PropertyOwnerVaspDetails,
  PropertyOwnerVaspListResponse,
  PropertyOwnerVaspStatusCounts,
  PropertyOwnerVaspQueueStatusCounts,
  RejectComplianceSubmissionPayload,
  RejectKycPayload,
  RejectPropertyOwnerPayload,
  ReviewPropertyOwnerBankAccountPayload,
  UploadKycDocumentInput,
  UpsertPropertyOwnerPayload,
} from '../types/propertyOwnerTypes'

import type { VaspApproveResult } from '@shared/constants/vaspSubmit'
import { parsePropertyOwnerApproval } from './approvalParser'
import {
  parsePropertyOwnerVaspDetails,
  parsePropertyOwnerVaspRow,
  parsePropertyOwnerVaspSubmitResponse,
  type PropertyOwnerVaspDetailsDto,
  type PropertyOwnerVaspSubmitResponseDto,
} from './vaspParser'

const PO = '/api/admin/property-owners'
const COMPLIANCE_AML = '/api/admin/compliance/aml'
const COMPLIANCE_PEP = '/api/admin/compliance/pep'
const PO_KYC = '/api/admin/property-owners/compliance/kyc'
const PO_BANK_ACCOUNTS = '/api/admin/property-owners/bank-accounts'
// VASP routes mount under `/api/vasp` (not `/api/admin/vasp`) — mirrors
// the existing investor VASP path convention (see investorsEndpointPaths.ts).
const PO_VASP = '/api/vasp/property-owner-users'
const PO_VASP_USERS_DETAILS = '/api/vasp/property-owner-users-details'

// Server-side maximum for the property-owner list `limit` param
// (GetPropertyOwnersRequestDto @Max(100) in admin-service). The owner picker
// is an unpaginated dropdown, so it asks for the largest page allowed.
const OWNER_PICKER_PAGE_LIMIT = 100

// Strip empty-string + null query params so axios doesn't send literal
// `?search=` (or `?propertyOwnerStatus=`) to the backend. Some NestJS
// ValidationPipes treat the bare empty value as a failed @IsString check
// and 400 the request — defensive convention used elsewhere in the PO API.
function stripEmpty<T extends Record<string, unknown>>(params: T): Partial<T> {
  const out: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue
    out[key] = value
  }
  return out as Partial<T>
}

// Onboarding-review list — dedicated server endpoint whose service hard-locks
// propertyOwnerStatus=PENDING_REVIEW + kycStatus=APPROVED. Use this instead of
// the generic list when rendering /property-owners/pending so the queue cleanly
// separates from /property-owners/kyc-review. Mirrors the investor
// `GET /investors/onboarding-review` pattern.
export const getOnboardingReviewPropertyOwnerList = async (
  params: GetPropertyOwnerListParams,
): Promise<PropertyOwnerListResponse> => {
  const { data } = await api.get(`${PO}/onboarding-review`, { params: stripEmpty(params) })
  const envelope = data?.data ?? data
  const rows = Array.isArray(envelope?.rows)
    ? envelope.rows
    : Array.isArray(envelope)
      ? envelope
      : []
  return {
    rows: rows.map(parsePropertyOwnerApproval) as PropertyOwner[],
    total: envelope?.total ?? rows.length,
    page: envelope?.page ?? 1,
    limit: envelope?.limit ?? 20,
  }
}

export const getPropertyOwnerList = async (
  params: GetPropertyOwnerListParams,
): Promise<PropertyOwnerListResponse> => {
  const { data } = await api.get(PO, { params: stripEmpty(params) })
  const envelope = data?.data ?? data
  const rows = Array.isArray(envelope?.rows)
    ? envelope.rows
    : Array.isArray(envelope)
      ? envelope
      : []
  return {
    rows: rows.map(parsePropertyOwnerApproval) as PropertyOwner[],
    total: envelope?.total ?? rows.length,
    page: envelope?.page ?? 1,
    limit: envelope?.limit ?? 20,
  }
}

export const getPropertyOwnerById = async (id: string): Promise<PropertyOwner> => {
  const { data } = await api.get(`${PO}/${id}`)
  return parsePropertyOwnerApproval(data?.data ?? data) as PropertyOwner
}

export const addPropertyOwner = async (payload: UpsertPropertyOwnerPayload): Promise<unknown> => {
  const { data } = await api.post(PO, payload)
  return data
}

export const updatePropertyOwner = async (
  id: string,
  payload: UpsertPropertyOwnerPayload,
): Promise<unknown> => {
  const { data } = await api.patch(`${PO}/${id}`, payload)
  return data
}

export const deletePropertyOwner = async (id: string): Promise<void> => {
  await api.delete(`${PO}/${id}`)
}

export const approvePropertyOwner = async (id: string): Promise<void> => {
  await api.patch(`${PO}/${id}/approve`)
}

export const rejectPropertyOwner = async (
  id: string,
  payload: RejectPropertyOwnerPayload,
): Promise<void> => {
  await api.patch(`${PO}/${id}/reject`, payload)
}

export const blockPropertyOwner = async (
  id: string,
  payload: { reason: string },
): Promise<void> => {
  await api.patch(`${PO}/${id}/block`, payload)
}

export const unblockPropertyOwner = async (id: string): Promise<void> => {
  await api.patch(`${PO}/${id}/unblock`)
}

export const unrejectPropertyOwner = async (
  id: string,
  payload: { reason?: string | null },
): Promise<void> => {
  await api.patch(`${PO}/${id}/un-reject`, payload)
}

export const getPropertyOwnerComplianceProfile = async (
  id: string,
): Promise<PropertyOwnerComplianceProfile> => {
  const { data } = await api.get(`${PO}/${id}/compliance-profile`)
  return (data?.data ?? data) as PropertyOwnerComplianceProfile
}

// Onboarding-review completion signals. The onboarding-review queue calls this
// per visible row to hide the approve/reject AML/PEP actions once those checks
// are already verified. Mirrors the investor onboarding-checks endpoint.
export const getPropertyOwnerOnboardingChecks = async (
  id: string,
): Promise<PropertyOwnerOnboardingChecks> => {
  const { data } = await api.get(`${PO}/${id}/onboarding-checks`)
  return (data?.data ?? data) as PropertyOwnerOnboardingChecks
}

// Company profile (entity-type POs only). admin-service commit 1570186.
// 404 for individual POs; consumers branch on the error.
export const getPropertyOwnerCompanyProfile = async (
  id: string,
): Promise<PropertyOwnerCompanyProfile> => {
  const { data } = await api.get(`${PO}/${id}/company-profile`)
  return (data?.data ?? data) as PropertyOwnerCompanyProfile
}

// PO onboarding pipeline list — admin-service commit 31eeab8. Accepts
// (step?, firstName?, lastName?, email?, page?, limit?) per the parity
// with the investor pipeline endpoint.
export interface GetPropertyOwnerOnboardingPipelineParams {
  step?: string
  firstName?: string
  lastName?: string
  email?: string
  page?: number
  limit?: number
}

export const getPropertyOwnerOnboardingPipeline = async (
  params: GetPropertyOwnerOnboardingPipelineParams,
): Promise<{
  rows: PropertyOwnerOnboardingPipelineRow[]
  total: number
}> => {
  const apiParams: Record<string, unknown> = {}
  if (params.step) apiParams.step = params.step
  if (params.firstName) apiParams.firstName = params.firstName
  if (params.lastName) apiParams.lastName = params.lastName
  if (params.email) apiParams.email = params.email
  if (params.page) apiParams.page = params.page
  if (params.limit) apiParams.limit = params.limit
  const { data } = await api.get('/api/admin/onboarding/property-owners/pipeline', {
    params: apiParams,
  })
  const envelope = data?.data ?? data
  const rows: PropertyOwnerOnboardingPipelineRow[] = Array.isArray(envelope?.data)
    ? envelope.data
    : Array.isArray(envelope)
      ? envelope
      : []
  const total = Number(envelope?.meta?.totalItems ?? envelope?.meta?.total ?? rows.length)
  return { rows, total }
}

// Per-owner compliance history feeds (KYC / AML / PEP submissions).
// admin-service commit 4d994b4. Same row shape as the investor side.
async function getPropertyOwnerComplianceFeed<TRow>(
  ownerId: string,
  kind: 'kyc' | 'aml' | 'pep',
  page: number,
  limit: number,
): Promise<{
  data: TRow[]
  pagination: { page: number; limit: number; totalItems: number; totalPages: number }
}> {
  const { data } = await api.get(`${PO}/${ownerId}/compliance/${kind}`, {
    params: { page, limit },
  })
  const envelope = data?.data ?? data
  const rows: TRow[] = Array.isArray(envelope?.data)
    ? envelope.data
    : Array.isArray(envelope)
      ? envelope
      : []
  const meta = envelope?.meta ?? {}
  const totalItems = Number(meta.totalItems ?? meta.total ?? rows.length)
  const effectiveLimit = Number(meta.limit ?? limit) || 1
  const totalPages = Number(meta.totalPages ?? Math.max(1, Math.ceil(totalItems / effectiveLimit)))
  return {
    data: rows,
    pagination: {
      page: Number(meta.page ?? page),
      limit: effectiveLimit,
      totalItems,
      totalPages,
    },
  }
}

export const getPropertyOwnerKycSubmissions = (ownerId: string, page: number, limit: number) =>
  getPropertyOwnerComplianceFeed<PropertyOwnerKycSubmissionRow>(ownerId, 'kyc', page, limit)

// Short-lived signed URL for a single KYC document. The list endpoint
// masks storage paths; this is the per-doc reveal hit on demand.
// admin-service commit 9c23abc.
export const getPropertyOwnerKycDocumentSignedUrl = async (
  ownerId: string,
  docId: string,
): Promise<PropertyOwnerKycDocumentSignedUrl> => {
  const { data } = await api.get(`${PO}/${ownerId}/kyc-documents/${docId}/signed-url`)
  return (data?.data ?? data) as PropertyOwnerKycDocumentSignedUrl
}

export const getPropertyOwnerAmlSubmissions = (ownerId: string, page: number, limit: number) =>
  getPropertyOwnerComplianceFeed<PropertyOwnerAmlSubmissionRow>(ownerId, 'aml', page, limit)

export const getPropertyOwnerPepSubmissions = (ownerId: string, page: number, limit: number) =>
  getPropertyOwnerComplianceFeed<PropertyOwnerPepSubmissionRow>(ownerId, 'pep', page, limit)

// KYC documents — paginated, newest first, all versions.
export const getPropertyOwnerKycDocuments = async (
  ownerId: string,
  page: number,
  limit: number,
): Promise<PropertyOwnerKycDocumentsResponse> => {
  const { data } = await api.get(`${PO}/${ownerId}/kyc-documents`, { params: { page, limit } })
  const envelope = data?.data ?? data
  const rows: PropertyOwnerKycDocument[] = Array.isArray(envelope?.data)
    ? envelope.data
    : Array.isArray(envelope)
      ? envelope
      : []
  const meta = envelope?.meta ?? {}
  const totalItems = Number(meta.totalItems ?? meta.total ?? rows.length)
  const effectiveLimit = Number(meta.limit ?? limit) || 1
  const totalPages = Number(meta.totalPages ?? Math.max(1, Math.ceil(totalItems / effectiveLimit)))
  return {
    data: rows,
    pagination: {
      page: Number(meta.page ?? page),
      limit: effectiveLimit,
      totalItems,
      totalPages,
    },
  }
}

// Compliance activity feed — paginated audit-log entries (KYC / AML / PEP),
// newest first. Same wire shape as the investor compliance activity feed.
export const getPropertyOwnerComplianceActivity = async (
  ownerId: string,
  page: number,
  limit: number,
): Promise<PropertyOwnerComplianceActivityResponse> => {
  const { data } = await api.get(`${PO}/${ownerId}/compliance/activity`, {
    params: { page, limit },
  })
  const envelope = data?.data ?? data
  const rows: PropertyOwnerComplianceActivityRow[] = Array.isArray(envelope?.data)
    ? envelope.data
    : Array.isArray(envelope)
      ? envelope
      : []
  const meta = envelope?.meta ?? {}
  const totalItems = Number(meta.totalItems ?? meta.total ?? rows.length)
  const effectiveLimit = Number(meta.limit ?? limit) || 1
  const totalPages = Number(meta.totalPages ?? Math.max(1, Math.ceil(totalItems / effectiveLimit)))
  return {
    data: rows,
    pagination: {
      page: Number(meta.page ?? page),
      limit: effectiveLimit,
      totalItems,
      totalPages,
    },
  }
}

export const approveAmlSubmission = async (
  amlId: string,
  payload: ApproveAmlPayload,
): Promise<void> => {
  await api.patch(`${COMPLIANCE_AML}/${amlId}/approve`, payload)
}

export const rejectAmlSubmission = async (
  amlId: string,
  payload: RejectComplianceSubmissionPayload,
): Promise<void> => {
  await api.patch(`${COMPLIANCE_AML}/${amlId}/reject`, payload)
}

export const approvePepSubmission = async (
  pepId: string,
  payload: ApprovePepPayload,
): Promise<void> => {
  await api.patch(`${COMPLIANCE_PEP}/${pepId}/approve`, payload)
}

export const rejectPepSubmission = async (
  pepId: string,
  payload: RejectComplianceSubmissionPayload,
): Promise<void> => {
  await api.patch(`${COMPLIANCE_PEP}/${pepId}/reject`, payload)
}

export const getPropertyOwnerKycList = async (
  params: GetPropertyOwnerKycListParams,
): Promise<PropertyOwnerKycListResponse> => {
  const { data } = await api.get(PO_KYC, { params: stripEmpty(params) })
  const envelope = data?.data ?? data
  const rawRows = Array.isArray(envelope?.data)
    ? envelope.data
    : Array.isArray(envelope?.rows)
      ? envelope.rows
      : Array.isArray(envelope)
        ? envelope
        : []
  const meta = envelope?.meta ?? envelope ?? {}
  return {
    rows: rawRows as PropertyOwnerKycListResponse['rows'],
    total: meta?.totalItems ?? meta?.total ?? rawRows.length,
    page: meta?.page ?? params.page ?? 1,
    limit: meta?.limit ?? params.limit ?? 20,
  }
}

// Multipart approve — mirrors the investor KYC approve endpoint shape.
// FormData carries `document` (boolean string), `decisionSource`, optional
// `reviewNotes`, the `file` upload, plus address + document metadata fields
// when `document=true`. The backend's `attachAddressAndDocument` writes the
// PO address + user_documents row in the same transaction as the status flip.
export const approvePropertyOwnerKycSubmission = async (
  kycId: string,
  formData: FormData,
): Promise<void> => {
  await api.patch(`${PO_KYC}/${kycId}/approve`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

export const rejectPropertyOwnerKycSubmission = async (
  kycId: string,
  payload: RejectKycPayload,
): Promise<void> => {
  await api.patch(`${PO_KYC}/${kycId}/reject`, payload)
}

export const getPropertyOwnerBankAccountsAdminList = async (
  params: GetPropertyOwnerBankAccountsAdminParams,
): Promise<PropertyOwnerBankAccountListResponse> => {
  const { data } = await api.get(PO_BANK_ACCOUNTS, { params: stripEmpty(params) })
  const envelope = data?.data ?? data
  const rawRows = Array.isArray(envelope?.rows)
    ? envelope.rows
    : Array.isArray(envelope?.data)
      ? envelope.data
      : Array.isArray(envelope)
        ? envelope
        : []
  const meta = envelope?.meta ?? envelope ?? {}
  return {
    rows: rawRows as PropertyOwnerBankAccountListResponse['rows'],
    total: meta?.totalItems ?? meta?.total ?? envelope?.rowsCount ?? rawRows.length,
    page: meta?.page ?? params.page ?? 1,
    limit: meta?.limit ?? params.limit ?? 20,
  }
}

export const getPropertyOwnerBankAccountStatusCounts =
  async (): Promise<PropertyOwnerBankAccountStatusCounts> => {
    const { data } = await api.get(`${PO_BANK_ACCOUNTS}/status-counts`)
    const env = data?.data ?? data
    return {
      pending: Number(env?.pending ?? 0),
      verified: Number(env?.verified ?? 0),
      rejected: Number(env?.rejected ?? 0),
    }
  }

// Detail call — the only place the API returns decrypted account number +
// IBAN. The list endpoint omits them so plaintext only travels over the wire
// when the operator drills into a row.
export const getPropertyOwnerBankAccountDetail = async (
  id: string,
): Promise<PropertyOwnerBankAccountRow> => {
  const { data } = await api.get(`${PO_BANK_ACCOUNTS}/${id}`)
  return (data?.data ?? data) as PropertyOwnerBankAccountRow
}

export const reviewPropertyOwnerBankAccount = async (
  id: string,
  payload: ReviewPropertyOwnerBankAccountPayload,
): Promise<void> => {
  await api.patch(`${PO_BANK_ACCOUNTS}/${id}`, payload)
}

export const createPropertyOwnerBankAccount = async (
  propertyOwnerId: string,
  payload: CreatePropertyOwnerBankAccountPayload,
): Promise<void> => {
  await api.post(`${PO}/${propertyOwnerId}/bank-accounts`, payload)
}

export const getPropertyOwnerVaspStatusCounts =
  async (): Promise<PropertyOwnerVaspStatusCounts> => {
    const { data } = await api.get(`${PO_VASP}/status-counts`)
    const env = data?.data ?? data
    return { pendingVaspApproval: Number(env?.pendingVaspApproval ?? 0) }
  }

// Counts for the 4 VASP queue filter cards. The /status-counts rollup only
// returns pendingVaspApproval, so each card derives its total from the VASP
// list endpoint (limit:1, reading total) per status bucket. Buckets are
// disjoint and cover the queue, so all = pending + active + rejected.
export const getPropertyOwnerVaspQueueStatusCounts =
  async (): Promise<PropertyOwnerVaspQueueStatusCounts> => {
    const [pending, active, rejected] = await Promise.all([
      getPropertyOwnerVaspList({
        page: 1,
        limit: 1,
        vaspUserStatus: VASP_CARD_STATUS_FILTERS.pending,
      }),
      getPropertyOwnerVaspList({
        page: 1,
        limit: 1,
        vaspUserStatus: VASP_CARD_STATUS_FILTERS.active,
      }),
      getPropertyOwnerVaspList({
        page: 1,
        limit: 1,
        vaspUserStatus: VASP_CARD_STATUS_FILTERS.rejected,
      }),
    ])
    return {
      pending: pending.total,
      active: active.total,
      rejected: rejected.total,
      all: pending.total + active.total + rejected.total,
    }
  }

export const getPropertyOwnerVaspList = async (
  params: GetPropertyOwnerVaspListParams,
): Promise<PropertyOwnerVaspListResponse> => {
  const { data } = await api.get(PO_VASP, { params: stripEmpty(params) })
  const envelope = data?.data ?? data
  const rawRows = Array.isArray(envelope?.data)
    ? envelope.data
    : Array.isArray(envelope?.rows)
      ? envelope.rows
      : Array.isArray(envelope)
        ? envelope
        : []
  const meta = envelope?.meta ?? envelope ?? {}
  return {
    rows: (rawRows as PropertyOwnerVaspDetailsDto[]).map(parsePropertyOwnerVaspRow),
    total: meta?.totalItems ?? meta?.total ?? rawRows.length,
    page: meta?.page ?? params.page ?? 1,
    limit: meta?.limit ?? params.limit ?? 20,
  }
}

export const getPropertyOwnerVaspDetails = async (
  id: string,
): Promise<PropertyOwnerVaspDetails> => {
  const { data } = await api.get(`${PO_VASP_USERS_DETAILS}/${id}`)
  return parsePropertyOwnerVaspDetails(data?.data ?? data)
}

/**
 * Approving *submits* the Ctrl-Alt organisation rather than PATCHing a status
 * onto it. Ctrl-Alt is explicit that a caller never sets a status directly — you
 * submit the object, the flow runs its checks (DLD participant verification
 * here), and the outcome is what moves the status.
 *
 * No step is sent: admin-service owns which lifecycle step its flow runs, and an
 * operator clicking Approve is not choosing a vendor lifecycle step.
 *
 * The resolved `vaspUserStatus` is the reconciled one: admin-service re-reads the
 * organisation after the submit, because a passing step is a compliance check and
 * not an activation. Branch on that, never on the step outcome alone.
 *
 * Resolves for `pass` and `pending`. A step that ran and *failed* arrives on the
 * error path as a 409 carrying this same shape — read it with `readSubmitSteps`
 * from @shared/constants/vaspSubmit, not from a resolved value here.
 */
export const approvePropertyOwnerVaspUser = async (
  vaspUserId: string,
): Promise<VaspApproveResult> => {
  const { data } = await api.post(`${PO_VASP}/${vaspUserId}/approve`)
  return parsePropertyOwnerVaspSubmitResponse(
    (data?.data ?? data ?? {}) as PropertyOwnerVaspSubmitResponseDto,
  )
}

/**
 * Resubmits the registration after the operator corrected the property owner's
 * Emirates ID on their own record — the resend half of edit-before-resend.
 *
 * Takes no body: the identifier is read server-side from the owner record, so the
 * resubmit can never disagree with what the operator actually saved. Same
 * `{ id, steps[] }` contract as approve, including a failed step arriving as a 409.
 */
export const resubmitPropertyOwnerVaspUser = async (
  vaspUserId: string,
): Promise<VaspApproveResult> => {
  const { data } = await api.post(`${PO_VASP}/${vaspUserId}/resubmit`)
  return parsePropertyOwnerVaspSubmitResponse(
    (data?.data ?? data ?? {}) as PropertyOwnerVaspSubmitResponseDto,
  )
}

export const rejectPropertyOwnerVaspUser = async (
  id: string,
  payload: { rejectionReason: string },
): Promise<void> => {
  await api.post(`${PO_VASP}/${id}/reject`, payload)
}

export const uploadKycDocument = async (
  id: string,
  input: UploadKycDocumentInput,
): Promise<void> => {
  // Camelcase view-model → snake_case multipart at the wire seam only.
  const formData = new FormData()
  formData.append('document_type', input.documentType)
  if (input.documentTitle) formData.append('document_title', input.documentTitle)
  if (input.expiryDate) formData.append('expiry_date', input.expiryDate)
  formData.append('file', input.file)
  await api.post(`${PO}/${id}/kyc-documents`, formData)
}

// Lookup-shape projections consumed cross-module by properties
// CoOwner tab. Defensive against envelope variation (plain array, {data},
// {data:{rows}}, {rows}) and snake_case / camelCase field-name drift.

export const getPropertyOwnersLookup = async (): Promise<PropertyOwnerLookup[]> => {
  const { data } = await api.get(PO)
  return extractLookupRows(data)
    .map((raw) => {
      const firstName = String(raw.first_name ?? raw.firstName ?? '')
      const lastName = String(raw.last_name ?? raw.lastName ?? '')
      const fullName = [firstName, lastName].filter(Boolean).join(' ')
      return {
        id: String(raw.id ?? raw._id ?? raw.ownerId ?? raw.owner_id ?? ''),
        name:
          fullName ||
          String(
            raw.name ??
              raw.owner_name ??
              raw.ownerName ??
              raw.full_name ??
              raw.fullName ??
              raw.label ??
              '',
          ),
        email: raw.email ? String(raw.email) : undefined,
        phone: raw.phone ? String(raw.phone) : undefined,
      }
    })
    .filter((o) => o.id && o.name)
}

// Owners + their bank accounts in one call. Lets the properties create-wizard
// co-owner step drive both the owner picker and the per-owner bank picker from
// a single fetch. Defensive against envelope + field-name drift, same as the
// plain lookup above; the embedded bank-account array may arrive as
// `bankAccounts` (camelCase) or `bank_accounts`.
//
// The route is VASP-scoped server-side: it only returns owners whose Ctrl-Alt
// registration is active, so the picker can never offer an owner the property
// could not later be tokenised against. No client-side status filter needed.
//
// `limit` is sent explicitly because the endpoint defaults to 20 — without it
// the picker silently shows only the 20 most-recent owners. 100 is the DTO's
// server-side maximum. Past that the picker needs server-side search.
export const getPropertyOwnersWithBankAccounts = async (): Promise<
  PropertyOwnerWithBankAccounts[]
> => {
  const { data } = await api.get(`${PO}/with-bank-accounts`, {
    params: { limit: OWNER_PICKER_PAGE_LIMIT },
  })
  return extractLookupRows(data)
    .map((raw) => {
      const firstName = String(raw.first_name ?? raw.firstName ?? '')
      const lastName = String(raw.last_name ?? raw.lastName ?? '')
      const fullName = [firstName, lastName].filter(Boolean).join(' ')
      const rawBanks = raw.bankAccounts ?? raw.bank_accounts ?? []
      const bankAccounts: OwnerBankAccountOption[] = (Array.isArray(rawBanks) ? rawBanks : [])
        .map((entry) => {
          const b = entry as Record<string, unknown>
          const bankName = b.bank_name ?? b.bankName ?? b.bank ?? ''
          const iban = b.iban ?? b.IBAN ?? b.iban_masked ?? b.ibanMasked ?? ''
          const accountNumber =
            b.account_number ??
            b.accountNumber ??
            b.account_number_masked ??
            b.accountNumberMasked ??
            b.account ??
            iban ??
            ''
          return {
            id: String(b.id ?? b._id ?? b.bankAccountId ?? b.bank_account_id ?? ''),
            accountNumber: String(accountNumber),
            bankName: bankName ? String(bankName) : undefined,
            iban: iban ? String(iban) : undefined,
          }
        })
        .filter((b) => b.id)
      return {
        id: String(raw.id ?? raw._id ?? raw.ownerId ?? raw.owner_id ?? ''),
        name:
          fullName ||
          String(
            raw.name ??
              raw.owner_name ??
              raw.ownerName ??
              raw.full_name ??
              raw.fullName ??
              raw.label ??
              '',
          ),
        email: raw.email ? String(raw.email) : undefined,
        phone: raw.phone ? String(raw.phone) : undefined,
        bankAccounts,
      }
    })
    .filter((o) => o.id && o.name)
}

export const getOwnerBankAccountsLookup = async (
  ownerId: string,
): Promise<OwnerBankAccountLookup[]> => {
  const { data } = await api.get(`${PO}/${ownerId}/bank-accounts`)
  return extractLookupRows(data)
    .map((raw) => {
      const bankName = raw.bank_name ?? raw.bankName ?? raw.bank ?? ''
      const iban = raw.iban ?? raw.IBAN ?? ''
      return {
        id: String(raw.id ?? raw._id ?? raw.bankAccountId ?? raw.bank_account_id ?? ''),
        accountNumber: String(raw.account_number ?? raw.accountNumber ?? raw.account ?? iban ?? ''),
        bankName: bankName ? String(bankName) : undefined,
        iban: iban ? String(iban) : undefined,
      }
    })
    .filter((b) => b.id)
}

function extractLookupRows(response: unknown): Record<string, unknown>[] {
  if (Array.isArray(response)) return response as Record<string, unknown>[]
  const inner = (response as Record<string, unknown>)?.data
  if (Array.isArray(inner)) return inner as Record<string, unknown>[]
  if (inner && typeof inner === 'object') {
    const rows = (inner as Record<string, unknown>).rows
    if (Array.isArray(rows)) return rows as Record<string, unknown>[]
  }
  const topRows = (response as Record<string, unknown>)?.rows
  if (Array.isArray(topRows)) return topRows as Record<string, unknown>[]
  return []
}
